import Dexie, { Table } from "dexie";
import { jwtDecode } from "jwt-decode";
import {
  deleteExamPager,
  getExamList,
  getExamPaper,
  updateExamPager,
} from "./api";

export interface Option {
  id?: number;
  lastConfirmAt?: Date;
  editingExamId?: string;
  hideTour?: boolean;
  useCount?: number;
}

// 纸张尺寸类型定义
export type PaperSize =
  | "a4"
  | "letter"
  | "legal"
  | "a3"
  | "a5"
  | `custom-${string}`;
// 页面方向类型
export type Orientation = "portrait" | "landscape";

// 纸张尺寸接口
export interface PaperDimension {
  id?: number;
  width: number;
  height: number;
  name?: string; // 仅用于自定义尺寸
}

// 页面数据接口
export interface Page {
  id: string;
  content: string;
  examId: string;
  index?: number;
  createdAt?: Date;
  updatedAt?: Date;
  isSaved?: boolean;
}

export interface ExamPaper {
  id?: string;
  name: string;
  paperSize?: PaperSize;
  orientation?: Orientation;
  margin?: number;
  columns?: number;
  lastPrintAt?: Date;
  printCount?: number;
  isPublic?: boolean;
  createdBy?: string;
  publicAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  isSaved?: boolean;
}

export interface User {
  id: number;
  name?: string;
  // 头像
  avatar?: string;
  token: string;
  role?: string;
  expiredAt: Date;
}

export class StateDateDexie extends Dexie {
  options!: Table<Option>;
  exams!: Table<ExamPaper>;
  pages!: Table<Page>;
  dimensions!: Table<PaperDimension>;
  user!: Table<User>;

  getOptions() {
    return this.options.toCollection().first();
  }

  async getCurrentExamPapger(): Promise<
    (ExamPaper & { pages?: Page[] }) | null
  > {
    const id = (await this.getOptions())?.editingExamId;
    const exam: any = id
      ? await this.exams.get(id)
      : await this.exams.toCollection().first();
    if (!exam) return null;
    exam.pages = await this.pages
      .where("examId")
      .equals(exam.id!)
      .toArray()
      .then((pages) => pages.sort((a, b) => (a.index || 0) - (b.index || 0)));
    return exam;
  }

  async getUser() {
    const user = await this.user.toCollection().first();
    if (user && new Date().getTime() < user.expiredAt.getTime()) {
      return user;
    }
  }

  constructor() {
    super("jianguoke.exampager");
    this.version(14).stores({
      options: "++id",
      exams: "++id,name,createdAt,updatedAt",
      dimensions: "++id,name",
      pages: "++id,examId",
      user: "++id",
    });
  }

  async init() {
    if ((await this.options.count()) <= 0) {
      await this.options.add({
        useCount: 0,
      });
    }
    if ((await this.exams.count()) <= 0) {
      await this.addEmptyExam();
    }
    const params = new URLSearchParams(win.location.search);
    const examId = params.get("examPaperId");
    if (examId) {
      if (await this.exams.get(examId)) {
        await this.setOption("editingExamId", parseInt(examId));
      }
    }
    await this.sync();
  }

  async addEmptyExam() {
    const examId = await this.exams.add({
      id: crypto.randomUUID().replaceAll("-", ""),
      name: "试卷一",
      paperSize: "a4",
      orientation: "portrait",
      columns: 2,
      margin: 15,
    });
    await this.addPage({
      id: crypto.randomUUID().replaceAll("-", ""),
      content: "",
      examId: examId as string,
      index: 0,
    });
    await this.setOption("editingExamId", examId);
  }

  async loadUserExams() {
    const data = await getExamList();
    if (data?.length) {
      // await this.pages.clear();
      await this.exams.bulkPut(
        data.map((exam: any) => {
          return { ...exam, isSaved: true };
        })
      );
      const ids = data.map((exam: any) => exam.id);
      const toDelete = (
        await this.exams
          .filter((exam) => !ids.includes(exam.id) && !!exam.isSaved)
          .toArray()
      ).map((exam) => exam.id!);
      if (toDelete.length) {
        await this.exams.bulkDelete(toDelete);
      }
      const options = await this.getOptions();
      if (options?.editingExamId && toDelete.includes(options.editingExamId)) {
        await this.setOption("editingExamId", data[0].id);
      }
    }
  }

  async syncExamPager(examId: any) {
    const exam = await this.exams.get(examId);
    if (!exam) return;

    const data = await updateExamPager({
      ...exam,
      pages: await this.pages.where("examId").equals(examId).toArray(),
    });
    if (!data) return;
    await this.exams.update(examId, { ...data, isSaved: true });
    if (data?.pages?.length) {
      await this.pages.bulkPut(
        data?.pages.map((page: any) => ({ ...page, isSaved: true }))
      );
    }
  }

  async sync() {
    let updateTimer: { [key: string]: any } = {};
    let userTimer: any = null;
    this.user.hook("creating", async () => {
      clearTimeout(userTimer);
      userTimer = setTimeout(async () => {
        await this.loadUserExams();
      });
    });
    this.user.hook("deleting", async () => {
      clearTimeout(userTimer);
      userTimer = setTimeout(async () => {
        await this.exams.clear();
        await this.addEmptyExam();
      });
    });
    this.options.hook("updating", async (modifications: any) => {
      if ("editingExamId" in modifications) {
        if (modifications.editingExamId) {
          setTimeout(async () => {
            const exam = await this.exams.get(modifications.editingExamId);
            if (!exam || !exam.isSaved) return;
            const remoteExam = await getExamPaper(modifications.editingExamId);
            if (!remoteExam) return;
            const { pages, ...data } = remoteExam;
            const exists = await this.exams.get(data.id!);
            if (
              !exists ||
              new Date(data.updatedAt) > new Date(exists.updatedAt!)
            ) {
              await this.exams.put(data);
            }
            for (const page of pages) {
              const pageExists = await this.pages.get(page.id);
              if (
                !pageExists ||
                new Date(page.updatedAt) > new Date(pageExists.updatedAt!)
              ) {
                await this.pages.put(page);
              }
            }
          });
        }
      }
    });
    this.pages.hook("updating", async (modifications: any, pk, page) => {
      if ("content" in modifications || "index" in modifications) {
        clearTimeout(updateTimer[page.examId]);
        updateTimer[page.examId] = setTimeout(async () => {
          await this.syncExamPager(page.examId);
        }, 1500);
      }
    });
    this.pages.hook("deleting", async (primKey: any, page) => {
      if (!page.isSaved) return;
      clearTimeout(updateTimer[page.examId]);
      updateTimer[page.examId] = setTimeout(async () => {
        await this.syncExamPager(page.examId);
      });
    });
    this.exams.hook("deleting", async (primKey: any, obj: any) => {
      if (!obj.isSaved) return;
      clearTimeout(updateTimer[primKey]);
      updateTimer[primKey] = setTimeout(async () => {
        await deleteExamPager(primKey);
      });
    });
    this.exams.hook("creating", async (pk: any) => {
      clearTimeout(updateTimer[pk]);
      updateTimer[pk] = setTimeout(async () => {
        await this.syncExamPager(pk);
      }, 1000);
    });
    this.exams.hook("updating", async (mod: any, primKey: any) => {
      if (
        "name" in mod ||
        "paperSize" in mod ||
        "orientation" in mod ||
        "margin" in mod
      ) {
        clearTimeout(updateTimer[primKey]);
        updateTimer[primKey] = setTimeout(async () => {
          const user = await this.getUser();
          if (user) {
            await this.syncExamPager(primKey);
          }
        });
      }
    });
  }

  async login(token: string) {
    const decoded = jwtDecode(token) as any;

    if (decoded) {
      const cur = await this.getUser();
      if (cur?.id && cur?.id === decoded.id) {
        this.user.update(cur.id, {
          token,
          expiredAt: new Date(decoded.exp! * 1000),
        });
        return;
      }
      const user: User = {
        id: decoded.id,
        name: decoded.name,
        avatar: decoded.avatar,
        token,
        expiredAt: new Date(decoded.exp! * 1000),
      };
      await this.user.clear();
      await this.user.add(user);
    } else {
      throw new Error("无效的令牌");
    }
  }

  async logout() {
    await this.user.clear();
  }

  async addExam(exam: ExamPaper) {
    const examId = await this.exams.add({
      id: crypto.randomUUID().replaceAll("-", ""),
      ...exam,
    });
    const pageId = await this.addPage({
      id: crypto.randomUUID().replaceAll("-", ""),
      content: "",
      examId: examId as string,
      index: 0,
    });
    await this.setOption("editingExamId", examId);
    return {
      examId,
      pageId,
    };
  }

  async setOption(key: string, value: any) {
    const existing = await this.getOptions();
    if (existing) {
      await this.options.update(existing.id!, {
        [key]: value,
      });
    } else {
      await this.options.add({
        [key]: value,
      });
    }
  }

  async setPaperSize(paperSize: PaperSize) {
    const exam = await this.getCurrentExamPapger();
    if (exam) {
      await this.exams.update(exam.id!, {
        paperSize,
      });
    }
  }

  async setOrientation(orientation: Orientation) {
    const exam = await this.getCurrentExamPapger();
    if (exam) {
      await this.exams.update(exam.id!, {
        orientation,
      });
    }
  }

  async setMargin(margin: number) {
    const exam = await this.getCurrentExamPapger();
    if (exam) {
      await this.exams.update(exam.id!, {
        margin,
      });
    }
  }

  async setColumns(columns: number) {
    const exam = await this.getCurrentExamPapger();
    if (exam) {
      await this.exams.update(exam.id!, {
        columns,
      });
    }
  }

  async addPage(page: Page, index?: number) {
    if (index !== undefined) {
      const pages = await this.pages
        .where("examId")
        .equals(page.examId)
        .toArray();
      const afterPages = pages.slice(index);
      await this.pages.bulkPut(
        afterPages.map((p) => ({ ...p, index: p.index! + 1 }))
      );
      return await this.pages.add({ ...page, index });
    } else {
      page.index = await this.pages.where("examId").equals(page.examId).count();
      return await this.pages.add(page);
    }
  }

  async updatePage(id: string, page: Partial<Page>) {
    await this.pages.update(id, page);
  }

  async removePage(id: string) {
    const page = await this.pages.get(id);
    if (!page) return;
    await this.pages.delete(id);
    const pages = await this.pages
      .where("examId")
      .equals(page.examId)
      .toArray();
    const afterPages = pages.slice(page.index!);
    await this.pages.bulkPut(
      afterPages.map((p) => ({ ...p, index: p.index! - 1 }))
    );
  }
}

const win = global as any;
export const store = (win.store = new StateDateDexie());
